"use client";

import {
  useState,
  useEffect,
  useCallback,
  startTransition,
  useRef,
  type ChangeEvent,
  type ReactNode,
} from "react";
import Link from "next/link";
import { getMuhurat } from "@/lib/api/muhurat";
import type { MuhuratResponse } from "@/lib/api/muhurat";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { TimingCitySelector, type SavedCity } from "@/components/choghadiya";
import {
  RAHU_KAAL_HEADLINES,
  RahuSeoContent,
  WeeklyRahuReference,
  type CityWeeklyRahuReference,
} from "./RahuKaal.reference";

// Surfaces that are not URL-addressed keep the seeker's city in storage. The
// hubs address the city as `?city=` (owner decision 2026-09-23) and pass
// `initialCity` + `citySelector`, which bypass this store entirely.
const STORAGE_KEY = "rahu_kaal_city";

const DEFAULT_CITY_COORDS: SavedCity = {
  name: "New Delhi",
  latitude: 28.6139,
  longitude: 77.209,
};

// Restore the seeker's last city during initial state (non-addressed surfaces).
function getInitialCity(defaultCity: string): SavedCity {
  const fallback =
    defaultCity === DEFAULT_CITY_COORDS.name
      ? DEFAULT_CITY_COORDS
      : { ...DEFAULT_CITY_COORDS, name: defaultCity };
  if (typeof window === "undefined") return fallback;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return fallback;
    const parsed = JSON.parse(saved) as SavedCity;
    if (parsed.name && parsed.latitude && parsed.longitude) return parsed;
  } catch {
    /* a corrupt or blocked store just keeps the default city */
  }
  return fallback;
}

function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const DAY_NAMES_HI: Record<string, string> = {
  Monday: "सोमवार",
  Tuesday: "मंगलवार",
  Wednesday: "बुधवार",
  Thursday: "गुरुवार",
  Friday: "शुक्रवार",
  Saturday: "शनिवार",
  Sunday: "रविवार",
};

export interface RahuKaalProps {
  language?: "en" | "hi";
  /**
   * Overrides the generic headline so a city page's <h1> names its city.
   *
   * Without it all 198 cities in a family shipped the SAME <h1> — measured live
   * 2026-09-21: /rahu-kaal/mumbai/, /jaipur/ and /pune/ all read "Rahu Kaal
   * Today", and every /panchang/<city>/ read "Today's Panchang", while each
   * page's <title> named its city correctly. One heading across a whole
   * generated family is the templated-content signal this site is already being
   * penalised for. `ChoghadiyaHeader` has taken this prop all along; panchang
   * and rahu-kaal were simply never given it.
   */
  heading?: string;
  headingLevel?: "h1" | "h2";
  defaultCity?: string;
  initialDate?: string;
  initialResult?: MuhuratResponse | null;
  resultOffer: ReactNode;
  crossLinks: ReactNode;
  /**
   * City pages only. The weekly table computed from the page city's own sunrise
   * and sunset; it applies while the selector still shows that city.
   */
  cityWeeklyReference?: CityWeeklyRahuReference | null;
  /**
   * City pages pass false: their own explainer and FAQ already define Rahu Kaal,
   * so the generic "About Rahu Kaal" block was the same ~100 words a third time.
   */
  showAbout?: boolean;
  /**
   * The exact city the server rendered (`?city=` hubs). Without it a
   * non-default `defaultCity` NAME was paired with Delhi's coordinates, and the
   * seeker's stored city could replace the one the URL names.
   */
  initialCity?: SavedCity;
  /**
   * URL-addressed hubs pass their `?city=`/`?date=` selector form; it replaces
   * the in-page city search and date picker.
   */
  citySelector?: ReactNode;
}

export function RahuKaal({
  language = "en",
  heading,
  headingLevel = "h1",
  defaultCity = "New Delhi",
  initialDate,
  initialResult = null,
  resultOffer,
  crossLinks,
  cityWeeklyReference,
  showAbout,
  initialCity,
  citySelector,
}: RahuKaalProps) {
  const [loading, setLoading] = useState(!initialResult);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MuhuratResponse | null>(initialResult);
  const trackedInitialResult = useRef(false);
  const [selectedDate, setSelectedDate] = useState<string>(initialDate ?? toDateString(new Date()));
  const [city, setCity] = useState<SavedCity>(() => initialCity ?? getInitialCity(defaultCity));

  const handleCitySelect = useCallback((next: SavedCity) => {
    setCity(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* non-fatal: the city still applies for this visit */
    }
  }, []);

  const fetchData = useCallback(async (dateStr: string, forCity: SavedCity) => {
    startTransition(() => {
      setLoading(true);
      setError(null);
    });
    try {
      // Rule 25: the engine receives coordinates + IANA zone and returns
      // already-local time strings. Never convert times on the client.
      const response = await getMuhurat(
        dateStr,
        forCity.name,
        forCity.latitude,
        forCity.longitude,
        forCity.timezone
      );
      // Rule 62 #10: the authoritative response commits through normal
      // batched state, never a deferrable transition.
      setResult(response);
      trackCalculatorUsed({ calculator: "rahu-kaal" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Rahu Kaal data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (
      initialResult &&
      selectedDate === initialDate &&
      city.name === defaultCity &&
      !trackedInitialResult.current
    ) {
      trackedInitialResult.current = true;
      trackCalculatorUsed({ calculator: "rahu-kaal" });
      return;
    }

    fetchData(selectedDate, city);
  }, [city, defaultCity, fetchData, initialDate, initialResult, selectedDate]);

  const displayDate = new Date(selectedDate + "T00:00:00");
  const dayName = displayDate.toLocaleDateString("en-IN", { weekday: "long" });
  const dayNameHi = DAY_NAMES_HI[dayName] ?? "";
  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <RahuHeader
          language={language}
          heading={heading}
          headingLevel={headingLevel}
          displayDate={displayDate}
          dayNameHi={dayNameHi}
          city={city.name}
          country={city.country}
        />
        {citySelector ?? (
          <>
            <TimingCitySelector city={city} onCitySelect={handleCitySelect} />
            <RahuDatePicker selectedDate={selectedDate} onChange={handleDateChange} />
          </>
        )}

        <div className="min-h-[760px] sm:min-h-[700px]">
          {loading && <RahuLoading language={language} />}
          {error && !loading && (
            <RahuError error={error} onRetry={() => fetchData(selectedDate, city)} />
          )}
          {result && !loading && (
            <RahuResults
              result={result}
              dayName={dayName}
              resultOffer={resultOffer}
              cityWeeklyReference={cityWeeklyReference}
              isPageCity={city.name === defaultCity}
            />
          )}
        </div>

        <RahuAbout show={showAbout} language={language} />

        {crossLinks}
      </div>
    </div>
  );
}

function RahuHeader({
  heading,
  headingLevel: Heading = "h1",
  language,
  displayDate,
  dayNameHi,
  city,
  country,
}: {
  language: "en" | "hi";
  heading?: string;
  headingLevel?: "h1" | "h2";
  displayDate: Date;
  dayNameHi: string;
  city: string;
  country?: string;
}) {
  return (
    <div className="text-center mb-10">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-intent-brand">
        Rahu Kaal Calculator
      </p>
      <Heading className="text-[36px] sm:text-[40px] font-bold leading-tight text-ish-dark font-serif">
        {heading ?? RAHU_KAAL_HEADLINES[language]}
      </Heading>
      <p className="text-[18px] mt-1 text-ish-muted font-serif">आज का राहु काल</p>
      <p className="mt-2 text-body text-ish-muted">
        {formatDate(displayDate)} &middot; {dayNameHi}
      </p>
      <p className="mt-1 text-caption text-ish-faint">
        Calculated for {country ? `${city}, ${country}` : city} using Swiss Ephemeris
      </p>
    </div>
  );
}

function RahuDatePicker({
  selectedDate,
  onChange,
}: {
  selectedDate: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex justify-center mb-8">
      <div className="card-sacred inline-flex items-center gap-3 px-4 py-2.5">
        <label htmlFor="rahu-kaal-date" className="text-body-small font-semibold text-ish-dark">
          Check for another date:
        </label>
        <input
          id="rahu-kaal-date"
          type="date"
          value={selectedDate}
          onChange={onChange}
          className="input-sacred rounded-lg px-3 py-1.5 text-body text-ish-dark"
        />
      </div>
    </div>
  );
}

function RahuLoading({ language }: { language: "en" | "hi" }) {
  return (
    <div className="card-sacred p-8 text-center">
      <span className="inline-block w-6 h-6 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin mb-3" />
      <span className="text-[28px] sm:text-[40px] font-bold block text-ish-dark font-serif">
        {RAHU_KAAL_HEADLINES[language]}
      </span>
      <span className="mt-1 block font-serif text-[32px] font-bold text-error sm:text-[40px]">
        --:-- -- - --:-- --
      </span>
      <p className="text-body text-ish-muted">Loading Rahu Kaal timings...</p>
    </div>
  );
}

function RahuError({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="card-sacred p-6 text-center border-error-tint bg-error/5">
      <p className="text-body text-error">{error}</p>
      <button onClick={onRetry} className="mt-3 text-body-small font-semibold text-intent-brand">
        Try Again
      </button>
    </div>
  );
}

function RahuAbout({ show = true, language = "en" }: { show?: boolean; language?: "en" | "hi" }) {
  return show ? <RahuSeoContent language={language} /> : null;
}

export function RahuResults({
  result,
  dayName,
  resultOffer,
  cityWeeklyReference = null,
  isPageCity = false,
}: {
  result: MuhuratResponse;
  dayName: string;
  resultOffer: ReactNode;
  /** Applies only while the selector still shows the page's own city. */
  cityWeeklyReference?: CityWeeklyRahuReference | null;
  isPageCity?: boolean;
}) {
  return (
    <div className="space-y-6">
      <PrimaryRahuCard result={result} />
      {resultOffer}
      <SecondaryInauspiciousSlots result={result} />
      <PanchangSummaryCard result={result} />
      <WeeklyRahuReference
        dayName={dayName}
        cityReference={isPageCity ? cityWeeklyReference : null}
        selectedWindow={`${result.rahu_kaal.start} – ${result.rahu_kaal.end}`}
      />
      <KundaliCta />
    </div>
  );
}

function PrimaryRahuCard({ result }: { result: MuhuratResponse }) {
  return (
    <div className="card-sacred p-6 sm:p-8 text-center border-error/35 bg-error/5">
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-error">
        {result.rahu_kaal.label_hi}
      </span>
      <span className="block text-body-small font-semibold uppercase tracking-wider mb-3 text-error">
        Rahu Kaal
      </span>
      <span className="text-[32px] sm:text-[40px] font-bold block text-error font-serif">
        {result.rahu_kaal.start} - {result.rahu_kaal.end}
      </span>
      <p className="mt-3 text-body-small text-error">
        Avoid starting new activities during this period
      </p>
    </div>
  );
}

function SecondaryInauspiciousSlots({ result }: { result: MuhuratResponse }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <SecondarySlotCard
        label="Yamagandam"
        labelHi={result.yamagandam.label_hi}
        time={`${result.yamagandam.start} - ${result.yamagandam.end}`}
        description="Ruled by Yama · Avoid travel"
      />
      <SecondarySlotCard
        label="Gulika Kaal"
        labelHi={result.gulika_kaal.label_hi}
        time={`${result.gulika_kaal.start} - ${result.gulika_kaal.end}`}
        description="Ruled by Saturn · Avoid auspicious work"
      />
    </div>
  );
}

function SecondarySlotCard({
  label,
  labelHi,
  time,
  description,
}: {
  label: string;
  labelHi: string;
  time: string;
  description: string;
}) {
  return (
    <div className="card-sacred p-5 text-center border-error/20 bg-error/2">
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-error">
        {labelHi}
      </span>
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-error">
        {label}
      </span>
      <span className="text-[20px] font-bold text-error font-serif">{time}</span>
      <p className="mt-2 text-caption text-ish-muted">{description}</p>
    </div>
  );
}

function PanchangSummaryCard({ result }: { result: MuhuratResponse }) {
  return (
    <div className="card-sacred p-6 sm:p-8">
      <h2 className="text-[18px] font-bold mb-4 text-ish-dark font-serif">
        Today&apos;s Panchang Summary
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <PanchangFact
          label="Tithi"
          value={result.panchang.tithi.name_en}
          detail={`${result.panchang.tithi.name_hi} · ${pakshaLabel(result)}`}
        />
        <PanchangFact
          label="Nakshatra"
          value={result.panchang.nakshatra.name_en}
          detail={result.panchang.nakshatra.name_hi}
        />
        <PanchangFact
          label="Yoga"
          value={result.panchang.yoga.name_en}
          detail={result.panchang.yoga.name_hi}
        />
        <PanchangFact label="Day" value={result.vara.name_en} detail={result.vara.name_hi} />
      </div>
    </div>
  );
}

function pakshaLabel(result: MuhuratResponse) {
  return result.panchang.tithi.paksha === "shukla" ? "Shukla Paksha" : "Krishna Paksha";
}

function PanchangFact({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
        {label}
      </span>
      <span className="text-body font-bold text-ish-dark">{value}</span>
      <span className="block text-caption text-ish-faint">{detail}</span>
    </div>
  );
}

function KundaliCta() {
  return (
    <div className="text-center">
      <Link
        href="/free-kundali/"
        className="btn-sacred inline-block font-semibold py-3 px-6 rounded-lg text-body"
      >
        Get Your Free Kundali &rarr;
      </Link>
    </div>
  );
}
